import { useState } from 'react';
import { Link } from 'react-router-dom';

export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "How is this different from Meta's native CAPI?",
      answer: "Meta's CAPI requires developer implementation and manual configuration. We automate all of it. One click."
    },
    {
      question: "Will this work with my existing pixel?",
      answer: "Yes. We supplement your pixel—we don't replace it. We capture what it misses."
    },
    {
      question: "How long does setup actually take?",
      answer: "About 90 seconds. Install the app. Done."
    },
    {
      question: "What metrics do I see in the dashboard?",
      answer: "Revenue recovered, purchases recovered, and pixel health. Everything you need to confirm it's working and prove ROI — without the clutter."
    },
    {
      question: "Why are you so cheap?",
      answer: "We built a focused tool, not a platform. Essential metrics. Low overhead. Low price."
    },
    {
      question: "What happens after I join the waitlist?",
      answer: "We'll email you when we open access. Early members get first access and launch pricing. No spam."
    }
  ];

  return (
    <div className="bg-off-white">
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-hero relative overflow-hidden">
        <div className="bg-gradient-glow absolute inset-0 pointer-events-none"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <p className="text-olive-600 font-medium mb-4">
            iOS broke your Meta tracking. We fix it.
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-olive-900 mb-6 tracking-tight">
            Ad Data You Can Actually Trust.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-10 max-w-3xl mx-auto">
            Server-side tracking for Shopify that bypasses iOS 14+ and AdBlockers. See every conversion Facebook missed. One click to install. $49/mo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/waitlist"
              className="btn-primary inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white hover-lift"
            >
              Join the Waitlist →
            </Link>
            <Link
              to="/resources"
              className="btn-secondary inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg hover-lift"
            >
              Free Guide: How iOS Is Costing You $10K+ →
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-olive-900 text-center mb-12">
            Facebook Says You Made $50K. Your Bank Says Otherwise.
          </h2>
          <div className="space-y-6 mb-12">
            <div className="glass-card rounded-xl p-6 flex gap-4 hover-lift">
              <span className="text-3xl flex-shrink-0">📉</span>
              <p className="text-lg text-gray-700">
                <strong className="text-olive-900">30-60% of your conversions never reach Meta.</strong> iOS and AdBlockers intercept them before your pixel fires.
              </p>
            </div>
            <div className="glass-card rounded-xl p-6 flex gap-4 hover-lift">
              <span className="text-3xl flex-shrink-0">🎯</span>
              <p className="text-lg text-gray-700">
                <strong className="text-olive-900">Your ROAS is a lie.</strong> You're scaling losers and killing winners based on incomplete data.
              </p>
            </div>
            <div className="glass-card rounded-xl p-6 flex gap-4 hover-lift">
              <span className="text-3xl flex-shrink-0">💸</span>
              <p className="text-lg text-gray-700">
                <strong className="text-olive-900">The "fix" costs $300-500/mo</strong> and requires a developer and 47 dashboards you'll never use.
              </p>
            </div>
          </div>
          <p className="text-xl text-center text-olive-800 font-medium">
            There's a simpler way.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-olive-900 text-center mb-12">
            What If Fixing Your Tracking Took 90 Seconds?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="feature-card rounded-xl p-8 hover-lift">
              <div className="w-12 h-12 bg-olive-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-olive-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-olive-900 mb-3">One-Click Install</h3>
              <p className="text-gray-600">
                Shopify's native Web Pixel Extension. No code. No theme edits. No developers.
              </p>
            </div>
            <div className="feature-card rounded-xl p-8 hover-lift">
              <div className="w-12 h-12 bg-olive-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-olive-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-olive-900 mb-3">Server-Side Capture</h3>
              <p className="text-gray-600">
                Events hit our servers before iOS or AdBlockers can block them. Then straight to Meta's CAPI.
              </p>
            </div>
            <div className="feature-card rounded-xl p-8 hover-lift">
              <div className="w-12 h-12 bg-olive-100 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-olive-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-olive-900 mb-3">Essential Metrics. Zero Bloat.</h3>
              <p className="text-gray-600">
                Revenue recovered. Purchases recovered. Pixel health. Everything you need — without 47 dashboards you'll never open.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-olive-900 text-center mb-16">
            From Broken Tracking to Complete Data in 4 Steps
          </h2>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { step: "Install", desc: "One click. Activates automatically.", icon: "📦" },
              { step: "Capture", desc: "Page views, add-to-carts, checkouts, purchases. Server-side.", icon: "📡" },
              { step: "Send", desc: "Direct to Meta's Conversions API. Complete picture.", icon: "🚀" },
              { step: "See", desc: "Revenue recovered, purchases recovered, pixel health. Your proof it's working.", icon: "📊" }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="glass-card rounded-xl p-6 text-center h-full hover-lift">
                  <span className="text-4xl mb-4 block">{item.icon}</span>
                  <h3 className="text-lg font-bold text-olive-900 mb-2">{item.step}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-olive-400 text-2xl z-10">
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-olive-900 text-center mb-12">
            Built for Store Owners. Not Data Scientists.
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass-olive rounded-xl p-8">
              <h3 className="text-xl font-bold text-olive-800 mb-6 flex items-center gap-2">
                <svg className="w-6 h-6 text-olive-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                This is for you if:
              </h3>
              <ul className="space-y-4 text-gray-700">
                {[
                  "You spend $5K-$50K/mo on Meta and don't trust the numbers",
                  "You can't justify $300+/mo for tools you won't understand",
                  "You don't have a developer (and don't want to hire one)",
                  "You want essential metrics, not 47 dashboards"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-olive-600 mt-1">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-600 mb-6 flex items-center gap-2">
                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                This is NOT for you if:
              </h3>
              <ul className="space-y-4 text-gray-600">
                {[
                  "You're doing $10M+/year (you need enterprise solutions)",
                  "You sell on Amazon/Walmart (we're Shopify-only)",
                  "You want 15 attribution models (we're not that tool)",
                  "You spend under $2K/mo on ads (you won't see ROI)"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-gray-400 mt-1">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-olive-900 mb-6">
            Everything You Need. Nothing You Don't.
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Other tools bury you in dashboards, reports, and metrics you'll never use.
          </p>
          <p className="text-lg text-gray-700 mb-8">
            Sareto shows you what matters:
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="glass-card rounded-xl p-6 hover-lift">
              <div className="text-3xl mb-3">💰</div>
              <h3 className="font-bold text-olive-900 mb-2">Revenue Recovered</h3>
              <p className="text-sm text-gray-600">Dollars Facebook would have missed</p>
            </div>
            <div className="glass-card rounded-xl p-6 hover-lift">
              <div className="text-3xl mb-3">🛒</div>
              <h3 className="font-bold text-olive-900 mb-2">Purchases Recovered</h3>
              <p className="text-sm text-gray-600">Conversions your pixel didn't catch</p>
            </div>
            <div className="glass-card rounded-xl p-6 hover-lift">
              <div className="text-3xl mb-3">✅</div>
              <h3 className="font-bold text-olive-900 mb-2">Pixel Health</h3>
              <p className="text-sm text-gray-600">Confirmation your tracking is working</p>
            </div>
          </div>
          <p className="text-xl font-medium text-olive-900">
            Clean. Complete. No PhD required.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-olive-900 text-center mb-12">
            What You'd Pay Elsewhere
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse glass-card rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-olive-50 border-b border-olive-200">
                  <th className="text-left py-4 px-6 font-semibold text-gray-600"></th>
                  <th className="text-center py-4 px-6 font-bold text-olive-800 bg-olive-100">Sareto</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-600">Hyros</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-600">Elevar</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-600">Triple Whale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-olive-100">
                <tr>
                  <td className="py-4 px-6 font-medium text-olive-900">Price</td>
                  <td className="py-4 px-6 text-center font-bold text-olive-700 bg-olive-50">$49/mo</td>
                  <td className="py-4 px-6 text-center text-gray-600">$500+</td>
                  <td className="py-4 px-6 text-center text-gray-600">$250+</td>
                  <td className="py-4 px-6 text-center text-gray-600">$300+</td>
                </tr>
                <tr className="bg-cream">
                  <td className="py-4 px-6 font-medium text-olive-900">Setup</td>
                  <td className="py-4 px-6 text-center font-bold text-olive-700 bg-olive-50">90 seconds</td>
                  <td className="py-4 px-6 text-center text-gray-600">Days</td>
                  <td className="py-4 px-6 text-center text-gray-600">Hours</td>
                  <td className="py-4 px-6 text-center text-gray-600">Hours</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-medium text-olive-900">Developer needed?</td>
                  <td className="py-4 px-6 text-center font-bold text-olive-700 bg-olive-50">No</td>
                  <td className="py-4 px-6 text-center text-gray-600">Usually</td>
                  <td className="py-4 px-6 text-center text-gray-600">Sometimes</td>
                  <td className="py-4 px-6 text-center text-gray-600">Sometimes</td>
                </tr>
                <tr className="bg-cream">
                  <td className="py-4 px-6 font-medium text-olive-900">Dashboards</td>
                  <td className="py-4 px-6 text-center font-bold text-olive-700 bg-olive-50">Essential metrics</td>
                  <td className="py-4 px-6 text-center text-gray-600">15+</td>
                  <td className="py-4 px-6 text-center text-gray-600">10+</td>
                  <td className="py-4 px-6 text-center text-gray-600">20+</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-medium text-olive-900">Server-side CAPI</td>
                  <td className="py-4 px-6 text-center bg-olive-50">
                    <span className="text-olive-600 font-bold">✓</span>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <span className="text-olive-600">✓</span>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <span className="text-olive-600">✓</span>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <span className="text-olive-600">✓</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-center text-lg font-medium text-olive-900 mt-8">
            Same server-side tracking. 10x cheaper. Zero complexity.
          </p>
        </div>
      </section>

      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-olive-900 mb-6">
            $49/month. That's the whole pricing page.
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            No tiers. No usage limits. No setup fees. Cancel anytime.
          </p>
          <div className="glass-olive rounded-xl p-8 mb-8">
            <h3 className="text-lg font-semibold text-olive-900 mb-4">The math:</h3>
            <p className="text-gray-700">
              At $5K/mo ad spend, Sareto costs less than 1% of your budget. If we recover 10% of your lost data, that's <strong className="text-olive-800">$500 in clarity for $49</strong>.
            </p>
          </div>
          <Link
            to="/waitlist"
            className="btn-primary inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white hover-lift mb-4"
          >
            Join the Waitlist →
          </Link>
          <p className="text-sm text-gray-500">
            Currently in private beta. Waitlist members get early access + launch pricing.
          </p>
        </div>
      </section>

      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-olive-900 text-center mb-12">
            Quick Answers
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="glass-card rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-olive-50/50 transition-colors"
                >
                  <span className="font-semibold text-olive-900">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-olive-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 text-gray-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-olive-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Your Ads Deserve Data That Doesn't Lie.
          </h2>
          <p className="text-xl text-olive-200 mb-10">
            Stop guessing. Start knowing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/waitlist"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg bg-white text-olive-900 hover:bg-olive-50 transition-colors"
            >
              Join the Waitlist →
            </Link>
            <Link
              to="/resources"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg border-2 border-olive-500 text-olive-200 hover:bg-olive-800 transition-colors"
            >
              Get the Free iOS Tracking Guide →
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-olive-950 text-olive-300">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Sareto</h3>
              <p className="text-sm">Server-side tracking for Shopify. One click. $49/mo.</p>
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="pt-8 border-t border-olive-800 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            </div>
            <p>© 2025 Sareto</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
